<template>
  <div id="page-commercial-claim-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page
            :card-title="cardTitle"
            :switch-model="model.IsEnabled"
        >
          <vue-element-loading :active="busy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from "@/components/mixins/FormModelMixin";
import CardPage from '../../components/Views/CardPage';

const selectOptions = {
  showLabels: false,
  key: "id",
  trackBy: "id",
  label: "name",
};

export default {
  name: "CommercialClaimForm",
  components: {
    CardPage,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  mixins: [FormModelMixin],
  data() {
    return {
      model: {
        CommercialClaimNotificationName: '',
        ProductCategory_RiskCode_FK: null,
        ProductCategory_SubRiskCode_FK: null,
        CommercialClaimName: '',
        ClaimNotificationForm: '',
      },
      schema: {
        groups: [
          {
            legend: 'General',
            styleClasses: "w-full",
            fields: [
              {
                disabled: this.readonly,
                type: "input",
                inputType: "text",
                styleClasses: "w-1/3 test-class-three",
                label: "Claim Form Name",
                model: "CommercialClaimNotificationName",
                required: !this.readonly,
                validator: ["required", 'checkEmptyInput'],
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Risk Class",
                model: "ProductCategory_RiskCode_FK",
                required: !this.readonly,
                validator: "selectRequired",
                values: () => this.riskCodeOptions,
                selectOptions,
              },
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Sub Risk Class",
                model: "ProductCategory_SubRiskCode_FK",
                required: !this.readonly,
                validator: "selectRequired",
                values: () => this.subRiskCodeOptions,
                selectOptions,
              },
              {
                disabled: this.readonly,
                type: "input",
                inputType: "text",
                styleClasses: "w-1/3 test-class-three",
                label: "Online Form",
                model: "ClaimNotificationForm",
              },
              {
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3',
                label: 'Sales Country',
                model: 'SalesCountryType_FK',
                disabled: () => this.readonly,
                values: () => this.salesCountryOptions,
                selectOptions
              },
            ]
          },
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      wordings: [],
      riskCodeOptions: [],
      subRiskCodeOptions: [],
      salesCountryOptions: [],
      formReferences: [
          'form',
      ],
      options: [
        {
          endpoint: () => this.$api.productCategory.riskClasses(),
          optionsFieldName: "riskCodeOptions",
          nameFieldName: "ProductCategoryName",
          idFieldName: 'ProductCategory_RiskCode_FK',
        },
        {
          endpoint: () => this.$api.productCategory.subRiskClasses(),
          optionsFieldName: "subRiskCodeOptions",
          nameFieldName: "ProductCategoryName",
          idFieldName: 'ProductCategory_SubRiskCode_FK',
        },
        {
          endpoint: () => this.$api.commercialClaims.salesCountries(),
          optionsFieldName: 'salesCountryOptions',
          nameFieldName: 'Type',
          idFieldName: 'SalesCountryType_FK'
        },
      ],
      dynamicClaims: [],
    };
  },
  async created() {
    await this.setDynamicFields();
    await this.loadOpts();
  },
  methods: {
    setPreSelectedDynamicFields() {
      const dynamicClaims = this.value.dynamic_products;

      if (!dynamicClaims) {
        return;
      }

      dynamicClaims.forEach((dynamicClaim) => {
        let modelFieldName = dynamicClaim.type.Type.toLowerCase();
        modelFieldName = modelFieldName.replace(/\s/g, '');

        const selectedItem = {
          id: dynamicClaim.DynamicClaimLoader_PK,
          name: dynamicClaim.LoaderName
        };
        this.$set(this.model, modelFieldName, selectedItem)
      });
    },
    postOptions() {
      return new Promise(resolve => {
        this.setPreSelectedDynamicFields();
        resolve();
      })
    },
    async setDynamicFields() {
      const { data } = await this.$api.commercialClaims.getDynamicClaimsTypes()

      let dynamicFields = data.data;

      if (dynamicFields.length > 0) {
        const fields = dynamicFields.map((field) => {
          let modelName = field.Type.toLowerCase();
          modelName = modelName.replace(/\s/g, '');

          this.$emit('add-dynamic-claim', modelName);

          // Add this in for multiple option dropdowns
          this.dynamicClaims.push({
            field: modelName,
            options: [],
          })

          // Options field name is a reference to the actual object
          this.setDynamicClaimOfType(field.id, () =>
              this.dynamicClaims.find(({field}) => field === modelName)
          );

          return {
            disabled: this.readonly,
            type: "vueMultiSelect",
            inputType: "text",
            styleClasses: "w-1/3",
            label: field.Type,
            model: modelName,
            // This is also a reference to the object
            values: () => this.dynamicClaims.find(({field}) => field === modelName).options,
            selectOptions: {
              ...selectOptions,
              allowEmpty: true,
            },
          };
        })

        this.schema.groups.push({
          legend: 'Documents',
          styleClasses: "w-full",
          fields: fields
        })
      }
    },
    /**
     *
     * @param id of dynamic claim
     * @param optionsFieldName
     */
    setDynamicClaimOfType(id, optionsFieldName) {
      this.options.push({
        endpoint: () => this.$api.dynamicClaims.listByType(id),
        optionsFieldName: optionsFieldName,
        nameFieldName: 'LoaderName',
        idFieldName: 'DynamicLoader_PK',
      });
    }
  },
};
</script>

<style scoped>

</style>
